/**
 * AI Action Buttons Component
 * Renders action buttons based on AI response actions
 * Note: Navigation logic should be provided via onNavigate in AIChatProvider
 */

import { useState } from 'react';
import { Save, ExternalLink, Edit, FileText, Share2, Calendar, Loader2, MessageSquarePlus } from 'lucide-react';
import { useAIChat } from '../contexts/AIChatContext';
import type { AIAction, AIPreviewConfigAction } from '../types';

interface AIActionButtonsProps {
  actions: AIAction[];
}

export default function AIActionButtons({ actions }: AIActionButtonsProps) {
  const { applyAction, saveActivePreview, openChat, addAIMessage, onNavigate, session } = useAIChat();
  const [saving, setSaving] = useState(false);

  const handleSaveAsDraft = async (action: AIPreviewConfigAction) => {
    applyAction(action);
    setSaving(true);
    try {
      const id = await saveActivePreview();
      if (id) {
        const reportType = session?.conversationContext?.reportType || 'report';
        const isDashboard = reportType === 'dashboard';
        const reportName = action.suggestedName || session?.activePreview?.name || (isDashboard ? 'AI Generated Dashboard' : 'AI Generated Report');
        addAIMessage(
          `Saved as "${reportName}". Open the ${isDashboard ? 'dashboard' : 'report'} to continue editing.`,
          [
            // TODO: Share — needs POST /api/reports/:id/share (user/org/dept picker per §3.7 wireframe)
            // { type: 'navigate', target: 'share-dialog' },
            // TODO: Schedule — needs ScheduleDialog component (API exists in reportScheduleRoutes, no frontend wireframe yet)
            // { type: 'navigate', target: 'schedule-dialog' },
            { type: 'open-report', reportDefinitionId: id, reportName, reportType },
          ]
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAction = (action: AIAction) => {
    switch (action.type) {
      case 'preview-config':
        applyAction(action);
        break;

      case 'navigate':
        if (onNavigate) {
          onNavigate(action.target);
        }
        break;

      case 'open-report':
        if (onNavigate) {
          onNavigate('report', { 
            reportDefinitionId: action.reportDefinitionId,
            reportType: (action as any).reportType 
          });
        }
        break;

      case 'recommend-existing':
        if (onNavigate) {
          onNavigate('report', { reportDefinitionId: action.reportDefinitionId });
        }
        break;

      case 'apply-change':
        applyAction(action);
        break;
    }
  };

  const getActionButton = (action: AIAction, index: number) => {
    switch (action.type) {
      case 'preview-config':
        return (
          <div key={index} className="flex gap-2">
            <button
              onClick={() => handleSaveAsDraft(action as AIPreviewConfigAction)}
              disabled={saving}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-400 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              onClick={() => onNavigate && onNavigate('builder', { 
                configType: (action as AIPreviewConfigAction).config?.type || session?.conversationContext?.reportType,
                aiGenerated: true,
                previewConfig: (action as AIPreviewConfigAction).config
              })}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-400 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-4 h-4" />
              Open in Builder
            </button>
            <button
              onClick={() => openChat()}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-400 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              <MessageSquarePlus className="w-4 h-4" />
              Refine with AI
            </button>
          </div>
        );

      case 'recommend-existing':
        return (
          <button
            key={index}
            onClick={() => handleAction(action)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-400 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View {action.reportName}
            {action.matchScore && (
              <span className="text-xs text-gray-500">
                ({Math.round(action.matchScore * 100)}% match)
              </span>
            )}
          </button>
        );

      case 'navigate': {
        const navLabels: Record<string, string> = {
          library: 'Go to Library',
          builder: 'Open Builder',
          'schedule-dialog': 'Schedule',
          'share-dialog': 'Share',
        };
        const navIcons: Record<string, any> = {
          library: FileText,
          builder: Edit,
          'schedule-dialog': Calendar,
          'share-dialog': Share2,
        };
        const Icon = navIcons[action.target];
        return (
          <button
            key={index}
            onClick={() => handleAction(action)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-400 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Icon className="w-4 h-4" />
            {navLabels[action.target]}
          </button>
        );
      }

      case 'open-report':
        return (
          <button
            key={index}
            onClick={() => handleAction(action)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-400 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View Report
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((action, index) => getActionButton(action, index))}
    </div>
  );
}
